import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useState } from 'react';
import styled from '@emotion/styled';
// Lucide React 직접 import - Vite 번들링 최적화 및 초기화 순서 문제 해결
import { HelpCircle } from 'lucide-react';
import { Copy } from 'lucide-react';
import { ExternalLink } from 'lucide-react';
import { Info } from 'lucide-react';
import { Code } from 'lucide-react';
import { Button } from '@edusense/shared-ui-kit';
// 스타일 컴포넌트
const GuideContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: var(--theme-spacing-md);
  color: var(--theme-text);
`;
const GuideHeader = styled.div`
  margin-bottom: var(--theme-spacing-xl);
`;
const GuideTitle = styled.h2`
  font-size: var(--theme-font-size-xl);
  margin-bottom: var(--theme-spacing-sm);
  color: var(--theme-text);
`;
const GuideDescription = styled.p`
  color: var(--theme-text-secondary);
  line-height: 1.6;
`;
const BlockTypeCard = styled.div`
  background: var(--theme-paper);
  border-radius: var(--theme-radius-lg);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  margin-bottom: var(--theme-spacing-lg);
  overflow: hidden;
  transition: transform 0.2s, box-shadow 0.2s;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  }
`;
const BlockHeader = styled.div`
  background: var(--theme-primary);
  color: var(--theme-primary-text);
  padding: var(--theme-spacing-md);
  font-weight: var(--theme-font-weight-semibold);
  display: flex;
  align-items: center;
  justify-content: space-between;
`;
const BlockTitle = styled.h3`
  margin: 0;
  font-size: var(--theme-font-size-lg);
  display: flex;
  align-items: center;
  gap: 8px;
`;
const BlockContent = styled.div`
  padding: var(--theme-spacing-md);
`;
const CodeSample = styled.div`
  margin: var(--theme-spacing-md) 0;
  position: relative;
  background: var(--theme-paper-dark);
  padding: var(--theme-spacing-md);
  border-radius: var(--theme-radius-md);
  font-family: var(--theme-typography-font-family-mono);
  font-size: var(--theme-font-size-sm);
  overflow-x: auto;
  
  pre {
    margin: 0;
    white-space: pre-wrap;
  }
`;
const CopyButton = styled.button`
  position: absolute;
  top: var(--theme-spacing-xs);
  right: var(--theme-spacing-xs);
  background: var(--theme-paper);
  border: 1px solid var(--theme-border);
  border-radius: var(--theme-radius-sm);
  padding: 4px;
  cursor: pointer;
  opacity: 0.7;
  transition: opacity 0.2s;
  
  &:hover {
    opacity: 1;
  }
`;
const PreviewSection = styled.div`
  margin-top: var(--theme-spacing-md);
  padding: var(--theme-spacing-md);
  background: var(--theme-paper-light);
  border-radius: var(--theme-radius-md);
  border: 1px dashed var(--theme-border);
`;
const InfoBox = styled.div`
  background-color: var(--theme-info-bg);
  border: 1px solid var(--theme-info);
  border-radius: var(--theme-radius-md);
  padding: var(--theme-spacing-md);
  margin: var(--theme-spacing-md) 0;
  display: flex;
  align-items: flex-start;
  gap: var(--theme-spacing-sm);
  
  svg {
    color: var(--theme-info);
    flex-shrink: 0;
    margin-top: 2px;
  }
`;
const HelpIconWrapper = styled.div`
  position: relative;
  display: inline-flex;
  margin-left: var(--theme-spacing-xs);
  cursor: help;
`;
const Tooltip = styled.div`
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  background-color: var(--theme-paper-dark);
  color: var(--theme-text);
  padding: var(--theme-spacing-sm);
  border-radius: var(--theme-radius-md);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  width: 250px;
  font-size: var(--theme-font-size-xs);
  font-weight: normal;
  z-index: 10;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.2s, visibility 0.2s;
  pointer-events: none;
  
  ${HelpIconWrapper}:hover & {
    opacity: 1;
    visibility: visible;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border-width: 5px;
    border-style: solid;
    border-color: var(--theme-paper-dark) transparent transparent transparent;
  }
`;
const HelpIcon = ({ tooltip })=>/*#__PURE__*/ _jsxs(HelpIconWrapper, {
        children: [
            /*#__PURE__*/ _jsx(HelpCircle, {
                size: 16,
                color: "var(--theme-primary)"
            }),
            /*#__PURE__*/ _jsx(Tooltip, {
                children: tooltip
            })
        ]
    });
// 블록 유형별 예제 코드
const codeExamples = {
    quiz: `:::quiz
\`\`\`yaml
# 객관식 퀴즈 예제
- id: python_basics
  type: multiple-choice
  question: "Python에서 리스트에 요소를 추가하는 메서드는 무엇인가요?"
  options:
    - "append()"
    - "insert()"
    - "extend()"
    - "add()"
  answerIndex: 0
  explanation: "append() 메서드는 리스트 끝에 요소를 추가합니다."
\`\`\`
:::`,
    shortAnswer: `:::quiz
\`\`\`yaml
# 주관식 퀴즈 예제
- id: python_version
  type: short-answer
  question: "Python의 현재 널리 사용되는 메이저 버전은 무엇인가요? (숫자만 입력)"
  answer: "3"
  explanation: "Python 3는 2008년에 출시되었으며 현재 가장 널리 사용되는 버전입니다."
\`\`\`
:::`,
    essay: `:::quiz
\`\`\`yaml
# 서술형 퀴즈 예제
- id: python_advantages
  type: essay
  question: "Python의 주요 장점 두 가지를 설명하세요."
  explanation: "Python의 장점으로는 쉬운 문법, 풍부한 라이브러리, 다양한 분야에서의 활용성, 커뮤니티 지원 등이 있습니다."
\`\`\`
:::`,
    codePractice: `:::code-practice
\`\`\`yaml
zipUrl: "/python/practice/algorithms.zip"
timeLimit: 30 # 분 단위
practiceType: "실습"
setupInstructions: |
  1. 제공된 zip 파일의 압축을 해제합니다.
  2. Python 개발 환경을 준비합니다.
  3. main.py 파일을 엽니다.
problemDescription: "정렬 알고리즘 구현하기"
requirements: |
  - 버블 정렬 함수를 구현하세요.
  - 입력: 정수 배열, 출력: 정렬된 배열
  - O(n²) 시간 복잡도를 가져야 합니다.
\`\`\`
:::`,
    glossary: `:::glossary
병원: hospital
코로나: COVID-19
오늘 병원에서 코로나 검사를 받아요.: I'm getting a COVID-19 test at the hospital today.
:::

오늘 병원에서 코로나 검사를 받아요.`
};
// 도움말 텍스트
const helpTexts = {
    quiz: "다양한 형식의 퀴즈를 지원합니다. 객관식, 주관식, 서술형 모두 동일한 quiz 블록을 사용하며 type 속성으로 구분합니다.",
    multipleChoice: "여러 선택지 중 하나를 선택하는 형식입니다. answerIndex는 0부터 시작합니다. 답안과 해설을 모두 포함해야 합니다.",
    shortAnswer: "텍스트를 입력하는 주관식 문제입니다. 정답은 문자열 또는 문자열 배열로 지정할 수 있습니다.",
    essay: "자유롭게 서술하는 형식의 문제입니다. 자동 채점되지 않으며 해설만 제공합니다.",
    codePractice: "ZIP 파일 기반 코딩 실습을 제공합니다. 시간 제한, 설정 안내, 문제 설명 등을 포함해야 합니다.",
    glossary: "단어 뜻은 드래그 조회용입니다. 분할 보기에는 문장 번역을 넣습니다. 문장 번역이 없는 단일 언어 강의만 구글 번역을 씁니다."
};
// 탭 관련 스타일
const TabsContainer = styled.div`
  margin-bottom: var(--theme-spacing-md);
`;
const TabsList = styled.div`
  display: flex;
  border-bottom: 1px solid var(--theme-border);
  margin-bottom: var(--theme-spacing-md);
`;
const TabButton = styled.button`
  padding: var(--theme-spacing-sm) var(--theme-spacing-md);
  background: transparent;
  border: none;
  border-bottom: 2px solid ${(props)=>props.isActive ? 'var(--theme-primary)' : 'transparent'};
  color: ${(props)=>props.isActive ? 'var(--theme-primary)' : 'var(--theme-text-secondary)'};
  font-weight: ${(props)=>props.isActive ? 'var(--theme-font-weight-semibold)' : 'var(--theme-font-weight-normal)'};
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    color: var(--theme-primary);
  }
  
  display: flex;
  align-items: center;
  gap: 4px;
`;
const TabContent = styled.div`
  /* 탭 콘텐츠 스타일 */
`;
const Tab = ({ children })=>{
    return /*#__PURE__*/ _jsx(_Fragment, {
        children: children
    });
};
const Tabs = ({ activeTab, onChange, children })=>{
    // children을 배열로 변환
    const tabsArray = React.Children.toArray(children);
    return /*#__PURE__*/ _jsxs(TabsContainer, {
        children: [
            /*#__PURE__*/ _jsx(TabsList, {
                children: tabsArray.map((tab)=>/*#__PURE__*/ _jsx(TabButton, {
                        isActive: activeTab === tab.props.id,
                        onClick: ()=>onChange(tab.props.id),
                        children: tab.props.label
                    }, tab.props.id))
            }),
            /*#__PURE__*/ _jsx(TabContent, {
                children: tabsArray.find((tab)=>tab.props.id === activeTab)
            })
        ]
    });
};
// 컴포넌트 메인
const CustomBlocksGuide = ()=>{
    const [activeTab, setActiveTab] = useState('quiz');
    const copyToClipboard = (text)=>{
        navigator.clipboard.writeText(text);
    // 여기에 복사 완료 알림 로직 추가 가능
    };
    return /*#__PURE__*/ _jsxs(GuideContainer, {
        children: [
            /*#__PURE__*/ _jsxs(GuideHeader, {
                children: [
                    /*#__PURE__*/ _jsx(GuideTitle, {
                        children: "커스텀 블록 가이드"
                    }),
                    /*#__PURE__*/ _jsx(GuideDescription, {
                        children: "마크다운에서 커스텀 블록을 사용하여 대화형 콘텐츠를 만들 수 있습니다. 각 블록 유형별 사용법과 예시를 확인하세요."
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs(Tabs, {
                activeTab: activeTab,
                onChange: setActiveTab,
                children: [
                    /*#__PURE__*/ _jsx(Tab, {
                        id: "quiz",
                        label: /*#__PURE__*/ _jsxs("span", {
                            children: [
                                "퀴즈 블록",
                                /*#__PURE__*/ _jsx(HelpIcon, {
                                    tooltip: helpTexts.quiz
                                })
                            ]
                        }),
                        children: /*#__PURE__*/ _jsxs(BlockTypeCard, {
                            children: [
                                /*#__PURE__*/ _jsx(BlockHeader, {
                                    children: /*#__PURE__*/ _jsxs(BlockTitle, {
                                        children: [
                                            "퀴즈 블록 (Quiz Block)",
                                            /*#__PURE__*/ _jsx(HelpIcon, {
                                                tooltip: "퀴즈 블록은 학습 평가를 위한 대화형 요소입니다. 여러 유형의 문제를 지원합니다."
                                            })
                                        ]
                                    })
                                }),
                                /*#__PURE__*/ _jsxs(BlockContent, {
                                    children: [
                                        /*#__PURE__*/ _jsx("p", {
                                            children: "퀴즈 블록은 객관식, 주관식, 서술형 등 다양한 유형의 문제를 제공할 수 있습니다."
                                        }),
                                        /*#__PURE__*/ _jsxs(InfoBox, {
                                            children: [
                                                /*#__PURE__*/ _jsx(Info, {
                                                    size: 20
                                                }),
                                                /*#__PURE__*/ _jsxs("div", {
                                                    children: [
                                                        /*#__PURE__*/ _jsx("strong", {
                                                            children: "사용 팁:"
                                                        }),
                                                        " type 속성으로 퀴즈 유형을 지정합니다. 지원되는 유형은 multiple-choice, short-answer, essay입니다."
                                                    ]
                                                })
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsxs("h4", {
                                            children: [
                                                "객관식 예제 ",
                                                /*#__PURE__*/ _jsx(HelpIcon, {
                                                    tooltip: helpTexts.multipleChoice
                                                })
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsxs(CodeSample, {
                                            children: [
                                                /*#__PURE__*/ _jsx(CopyButton, {
                                                    onClick: ()=>copyToClipboard(codeExamples.quiz),
                                                    children: /*#__PURE__*/ _jsx(Copy, {
                                                        size: 16
                                                    })
                                                }),
                                                /*#__PURE__*/ _jsx("pre", {
                                                    children: codeExamples.quiz
                                                })
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsxs("h4", {
                                            children: [
                                                "주관식 예제 ",
                                                /*#__PURE__*/ _jsx(HelpIcon, {
                                                    tooltip: helpTexts.shortAnswer
                                                })
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsxs(CodeSample, {
                                            children: [
                                                /*#__PURE__*/ _jsx(CopyButton, {
                                                    onClick: ()=>copyToClipboard(codeExamples.shortAnswer),
                                                    children: /*#__PURE__*/ _jsx(Copy, {
                                                        size: 16
                                                    })
                                                }),
                                                /*#__PURE__*/ _jsx("pre", {
                                                    children: codeExamples.shortAnswer
                                                })
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsxs("h4", {
                                            children: [
                                                "서술형 예제 ",
                                                /*#__PURE__*/ _jsx(HelpIcon, {
                                                    tooltip: helpTexts.essay
                                                })
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsxs(CodeSample, {
                                            children: [
                                                /*#__PURE__*/ _jsx(CopyButton, {
                                                    onClick: ()=>copyToClipboard(codeExamples.essay),
                                                    children: /*#__PURE__*/ _jsx(Copy, {
                                                        size: 16
                                                    })
                                                }),
                                                /*#__PURE__*/ _jsx("pre", {
                                                    children: codeExamples.essay
                                                })
                                            ]
                                        })
                                    ]
                                })
                            ]
                        })
                    }),
                    /*#__PURE__*/ _jsx(Tab, {
                        id: "codePractice",
                        label: /*#__PURE__*/ _jsxs("span", {
                            children: [
                                "코드 실습 블록",
                                /*#__PURE__*/ _jsx(HelpIcon, {
                                    tooltip: helpTexts.codePractice
                                })
                            ]
                        }),
                        children: /*#__PURE__*/ _jsxs(BlockTypeCard, {
                            children: [
                                /*#__PURE__*/ _jsx(BlockHeader, {
                                    children: /*#__PURE__*/ _jsxs(BlockTitle, {
                                        children: [
                                            "코드 실습 블록 (Code Practice Block)",
                                            /*#__PURE__*/ _jsx(HelpIcon, {
                                                tooltip: "코드 실습 블록은 ZIP 파일 기반의 코딩 실습을 제공합니다."
                                            })
                                        ]
                                    })
                                }),
                                /*#__PURE__*/ _jsxs(BlockContent, {
                                    children: [
                                        /*#__PURE__*/ _jsx("p", {
                                            children: "코드 실습 블록은 ZIP 파일을 다운로드하여 학습자가 직접 코딩을 수행하는 실습 문제를 제공합니다."
                                        }),
                                        /*#__PURE__*/ _jsxs(InfoBox, {
                                            children: [
                                                /*#__PURE__*/ _jsx(Code, {
                                                    size: 20
                                                }),
                                                /*#__PURE__*/ _jsxs("div", {
                                                    children: [
                                                        /*#__PURE__*/ _jsx("strong", {
                                                            children: "필수 속성:"
                                                        }),
                                                        " zipUrl, timeLimit, problemDescription, requirements는 반드시 포함되어야 합니다."
                                                    ]
                                                })
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsxs(CodeSample, {
                                            children: [
                                                /*#__PURE__*/ _jsx(CopyButton, {
                                                    onClick: ()=>copyToClipboard(codeExamples.codePractice),
                                                    children: /*#__PURE__*/ _jsx(Copy, {
                                                        size: 16
                                                    })
                                                }),
                                                /*#__PURE__*/ _jsx("pre", {
                                                    children: codeExamples.codePractice
                                                })
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsxs(InfoBox, {
                                            children: [
                                                /*#__PURE__*/ _jsx(Info, {
                                                    size: 20
                                                }),
                                                /*#__PURE__*/ _jsxs("div", {
                                                    children: [
                                                        /*#__PURE__*/ _jsx("strong", {
                                                            children: "참고:"
                                                        }),
                                                        " setupInstructions와 같은 긴 텍스트는 파이프 문자(|)를 사용하여 여러 줄로 작성할 수 있습니다. 각 줄은 적절히 들여쓰기 되어야 합니다."
                                                    ]
                                                })
                                            ]
                                        })
                                    ]
                                })
                            ]
                        })
                    }),
                    /*#__PURE__*/ _jsx(Tab, {
                        id: "glossary",
                        label: /*#__PURE__*/ _jsxs("span", {
                            children: [
                                "뜻 블록",
                                /*#__PURE__*/ _jsx(HelpIcon, {
                                    tooltip: helpTexts.glossary
                                })
                            ]
                        }),
                        children: /*#__PURE__*/ _jsxs(BlockTypeCard, {
                            children: [
                                /*#__PURE__*/ _jsx(BlockHeader, {
                                    children: /*#__PURE__*/ _jsxs(BlockTitle, {
                                        children: [
                                            "작성된 뜻 (Glossary)",
                                            /*#__PURE__*/ _jsx(HelpIcon, {
                                                tooltip: "드래그 조회와 분할 보기는 이 블록의 뜻만 사용합니다."
                                            })
                                        ]
                                    })
                                }),
                                /*#__PURE__*/ _jsxs(BlockContent, {
                                    children: [
                                        /*#__PURE__*/ _jsx("p", {
                                            children: "실시간 기계번역 없이, 강의에 적어 둔 뜻만 학습자에게 보여 줍니다."
                                        }),
                                        /*#__PURE__*/ _jsxs(CodeSample, {
                                            children: [
                                                /*#__PURE__*/ _jsx(CopyButton, {
                                                    onClick: ()=>copyToClipboard(codeExamples.glossary),
                                                    children: /*#__PURE__*/ _jsx(Copy, {
                                                        size: 16
                                                    })
                                                }),
                                                /*#__PURE__*/ _jsx("pre", {
                                                    children: codeExamples.glossary
                                                })
                                            ]
                                        })
                                    ]
                                })
                            ]
                        })
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs(InfoBox, {
                children: [
                    /*#__PURE__*/ _jsx(ExternalLink, {
                        size: 20
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        children: [
                            /*#__PURE__*/ _jsx("strong", {
                                children: "더 자세한 정보:"
                            }),
                            " 커스텀 블록의 고급 사용법과 추가 옵션에 대한 자세한 내용은",
                            /*#__PURE__*/ _jsx("a", {
                                href: "#",
                                style: {
                                    marginLeft: '4px'
                                },
                                children: "개발자 문서"
                            }),
                            "를 참조하세요."
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("div", {
                style: {
                    textAlign: 'center',
                    marginTop: 'var(--theme-spacing-lg)'
                },
                children: /*#__PURE__*/ _jsx(Button, {
                    children: "시작하기"
                })
            })
        ]
    });
};
